import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ImageActions from "@/actions/ImageActions";
import SiteActions from "@/actions/SiteActions";
import TransformActions from "@/actions/TransformActions";
import UIActions from "@/actions/UIActions";
import { useRetry } from "@/hooks/useRetry";
import DrawnToolStore from "@/stores/DrawnToolStore";
import SelectionStore from "@/stores/SelectionStore";
import useStoreState from "../providers/useStoreState";
import { getPosition, RAY_OFFSET_VALUE } from "./FloatingStageItem.utils.mjs";
import styles from "./FloatingStageItem.module.css";

/**
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS} LAYOUTS
 */

const RETRY_MAX = 1;

/** @type {Coordinates} */
const START_POSITION = [-RAY_OFFSET_VALUE, -RAY_OFFSET_VALUE];

// delay to wait for store data to reach component.
const STORE_DELAY = 100;

const REPOSITION_ACTIONS = [
  SiteActions.redo,
  SiteActions.resizeToAspectRatio,
  SiteActions.undo,
  SiteActions.updateBlock,
  TransformActions.endTransform,
  TransformActions.shift,
  UIActions.incrementZoom,
  UIActions.setZoom,
  UIActions.showLeftPanelView,
  UIActions.showRightPanelView,
  UIActions.stageWidthChange,
  UIActions.zoomReset,
  UIActions.zoomToWidth,
];

const FREEZE_HANDLERS = [
  ImageActions.exitAndSaveCropMode,
  ImageActions.exitCropMode,
];

const UNFREEZE_HANDLERS = [ImageActions.exitFinished];

/**
 * @typedef {import("@/types/MathTypes.mjs").Coordinates} Coordinates
 * @typedef {[HTMLElement | undefined, React.Dispatch<React.SetStateAction<HTMLElement | undefined>>]} RefStateSetter
 * @typedef {[Coordinates | undefined, React.Dispatch<React.SetStateAction<Coordinates>>]} CoordinateStateSetter
 */

const unsubscribe = (unsubscribers) =>
  unsubscribers.forEach((unsubscribe) => unsubscribe());

/**
 * @param {Object} props
 * @param {object} props.block - The selected block from the block container.
 * @param {React.ReactNode} props.children - The child elements to render.
 * @param {LAYOUTS[number]} props.layout - The current selected layout.
 * @param {boolean} props.getTarget - Collect target from selection store instead of from drawn transformer.
 * @param {HTMLElement} props.stageRef - The reference to the stage element.
 * @param {number} props.yOffset - Y offset from the top of the canvases container to the current canvas (in design space).
 * @param {number} props.zoom - The current zoom level.
 * @returns {import("react").JSX.Element}
 */
const InnerFloatingStageItem = ({
  block,
  children,
  getTarget,
  layout,
  stageRef: passedStageRef,
  yOffset,
  zoom,
}) => {
  const selectedElements = useStoreState(SelectionStore, () =>
    SelectionStore.getFullVisibleElementsJS(layout),
  );

  // The tool the transformer has drawn (same data as its SVG <g transform>).
  const drawnTool = useStoreState(DrawnToolStore, () =>
    DrawnToolStore.getDrawnTool(layout),
  );

  /** @type {RefStateSetter} */
  const [stageRef, setStageRef] = useState();

  /** @type {RefStateSetter} */
  const [selfRef, setSelfRef] = useState();

  /** @type {CoordinateStateSetter} */
  const [position, setPosition] = useState(START_POSITION);
  const [frozen, setFrozen] = useState(false);

  // The reposition-action timeouts below can outlive the render that
  // scheduled them (cleanup unsubscribes the listener, but a pending timeout
  // still fires). Reading the inputs through a ref makes any late callback
  // recompute from the latest committed values instead of replaying a stale
  // snapshot — e.g. an old drawnTool matrix paired with a new zoom, which
  // landed the toolbar off-center after zooming.
  const positionArgsRef = useRef();
  positionArgsRef.current = {
    block,
    drawnTool,
    layout,
    getTarget,
    selectedElements,
    self: selfRef,
    stage: stageRef,
    yOffset,
    zoom,
  };

  const tryReposition = () => {
    try {
      setPosition(getPosition(positionArgsRef.current));
      return true; // Success
    } catch (err) {
      return false; // Failure, will retry
    }
  };

  const [startRetries] = useRetry(
    tryReposition,
    () => setPosition(START_POSITION),
    RETRY_MAX,
  );

  const reposition = () => {
    if (!tryReposition()) startRetries();
  };

  // ref effect
  useEffect(() => {
    !stageRef && passedStageRef && setStageRef(passedStageRef);
  }, [passedStageRef]);

  // Position calc effect. Layout effect so the reposition happens in the
  // same pre-paint pass as the transformer's own DOM update — a deferred
  // reposition (passive effect or timeout) lets the browser paint a frame
  // with the toolbar at its stale position, which shows up as an
  // intermittent "pop" while dragging (most visibly in WebKit).
  useLayoutEffect(() => {
    if (!frozen && stageRef && selfRef) reposition();
  }, [stageRef, selfRef, getTarget, zoom, selectedElements, drawnTool, frozen]);

  // Reposition after actions whose effects aren't visible in the deps above
  // (e.g. panel toggles that move the stage without changing the tool).
  // Subscribed once (not per position change) since the callback reads
  // positionArgsRef at fire time.
  useLayoutEffect(() => {
    if (frozen) return undefined;

    const unsubscribers = REPOSITION_ACTIONS.map((action) =>
      // unfortunate setTimeout usage to wait for the stores to update
      action.listen(() => setTimeout(reposition, STORE_DELAY)),
    );

    return () => unsubscribe(unsubscribers);
  }, [frozen]);

  // freeze position to prevent reposition calc to let stores/rerender to catch up.
  useEffect(() => {
    const unsubscribers = FREEZE_HANDLERS.map((action) =>
      action.listen(() => setFrozen(true)),
    );
    return () => unsubscribe(unsubscribers);
  });

  // unfreeze and allow reposition calc to resume.
  useEffect(() => {
    const unsubscribers = UNFREEZE_HANDLERS.map((action) =>
      action.listen(() => setTimeout(() => setFrozen(false), STORE_DELAY)),
    );
    return () => unsubscribe(unsubscribers);
  });

  return (
    <div
      ref={setSelfRef}
      className={styles.floatingItem}
      style={{
        "--position-x": position[0],
        "--position-y": position[1],
      }}
    >
      {children}
    </div>
  );
};

/**
 * Gate: only mount the positioning machinery while there's something to
 * position (children) and an active selection to anchor to (`active`).
 */
const FloatingStageItem = (props) =>
  props.children && props.active ? <InnerFloatingStageItem {...props} /> : null;

export default FloatingStageItem;
